import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/user/driver_groups/_components/columns";
import { DeleteDriverResponse, Driver } from "@lib/types/driver";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { deleteDriver } from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { useSnackbar } from "@context/SnackBarProvider";
import { DriverGroup, DriverGroupsList } from "@lib/types/driver_group";
import { getDriverGroups } from "@lib/api/queries/driver_groups";
import CreateDriverGroup from "@pages/user/driver_groups/_components/createDriverGroup";
import UpdateDriverGroup from "@pages/user/driver_groups/_components/updateDriverGroup";
import ViewDriverGroup from "./_components/viewDriverGroup";

const DriverGroupsMain: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [addDriverGroupDialogOpen, setAddDriverGroupDialogOpen] =
    useState(false);
  const [updateDriverGroupDialogOpen, setUpdateDriverGroupDialogOpen] =
    useState(false);
  const [driverGroupToEdit, setDriverGroupToEdit] = useState<
    DriverGroup | undefined
  >(undefined);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [selectedDriverGroup, setSelectedDriverGroup] = useState<
    DriverGroup | undefined
  >(undefined);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const {
    data: driverGroups,
    isFetching,
    refetch,
    error,
  } = useQuery<DriverGroupsList>({
    queryKey: ["driver_groups"],
    queryFn: () => getDriverGroups(),
    refetchOnWindowFocus: false,
  });

  const { mutate: deleteDriverfn, isPending: isDeletingDriver } = useMutation({
    mutationFn: deleteDriver,
    onSuccess: (data: DeleteDriverResponse) => {
      showSnackbar(t("user.drivers.deleteSuccess"), "success");

      setSelectedDriverGroup(undefined);
      refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  // const confirmDeleteDriver = () => {
  //   if (!selectedDriverGroup) return;
  //   deleteDriverfn(selectedDriverGroup.id);
  //   setShowDeletePrompt(false);
  // };

  const handleDeleteDriver = (driverGroup: DriverGroup) => {
    setSelectedDriverGroup(driverGroup);
    setShowDeletePrompt(true);
  };

  const handleViewDriverGroup = (driverGroup: DriverGroup) => {
    setSelectedDriverGroup(driverGroup);
    setViewDialogOpen(true);
  };

  useEffect(() => {
    if (error instanceof AxiosError && error.response) {
      const errorDetails = handleError(error.response.data);
      errorDetails && showSnackbar(errorDetails.message, "error");
    }
  }, [error]);

  return (
    <Layout>
      <SectionHead title={t("user.driverGroups.header")}>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            onClick={() => {
              setAddDriverGroupDialogOpen(true);
            }}
            color="primary"
            className="flex items-center gap-1 p-2"
          >
            <Plus size={18} />
            <Typography variant="body2">
              {t("user.driverGroups.add")}
            </Typography>
          </Button>
        </div>
      </SectionHead>

      <SectionContent>
        {!error && (
          <div className="flex items-center justify-between">
            <TableList
              data={driverGroups?.data || []}
              columns={columns(
                setDriverGroupToEdit,
                setUpdateDriverGroupDialogOpen,
                handleViewDriverGroup
              )}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              isLoading={isFetching}
            />
          </div>
        )}

        <CreateDriverGroup
          addDriverGroupDialogOpen={addDriverGroupDialogOpen}
          setAddDriverGroupDialogOpen={setAddDriverGroupDialogOpen}
          refetch={refetch}
        />

        <UpdateDriverGroup
          updateDriverGroupDialogOpen={updateDriverGroupDialogOpen}
          setUpdateDriverGroupDialogOpen={setUpdateDriverGroupDialogOpen}
          driverGroupData={driverGroupToEdit}
          refetch={refetch}
        />

        <ViewDriverGroup
          viewDriverGroupDialogOpen={viewDialogOpen}
          setViewDriverGroupDialogOpen={setViewDialogOpen}
          driverGroupData={selectedDriverGroup}
        />
      </SectionContent>
    </Layout>
  );
};

export default DriverGroupsMain;
